import React, { useEffect, useState } from "react";
import api from "../api/axios";

function BalanceSheet() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [toDate, setToDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const token = localStorage.getItem("accessToken");

  const branch_id = sessionStorage.getItem("selectedContext")
    ? JSON.parse(sessionStorage.getItem("selectedContext")).branch
    : 0;

  useEffect(() => {
    fetchBalanceSheet();
  }, []);

  const fetchBalanceSheet = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/reports/balance-sheet?branch_id=${branch_id}&to_date=${toDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setData(res.data);
    } catch (err) {
      console.error("Error fetching Balance Sheet:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchBalanceSheet();
  };

  if (loading) return <div className="p-4 text-center">Loading Balance Sheet...</div>;
  if (!data) return <div className="p-4 text-center text-muted">No data available.</div>;

  return (
    <div className="container-fluid mt-3 print-container">
      {/* Filter Section - Hidden on Print */}
      <div className="card mb-3 d-print-none">
        <div className="card-body">
          <form onSubmit={handleFilterSubmit} className="row g-3 align-items-center">
            <div className="col-auto">
              <label className="col-form-label fw-bold">As On Date:</label>
            </div>
            <div className="col-auto">
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col-auto">
              <button type="submit" className="btn btn-primary">
                Filter
              </button>
            </div>
            <div className="col-auto ms-auto">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => window.print()}
              >
                Print PDF
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Printable Sheet */}
      <div className="card border-0">
        <div className="card-body p-0">
          <h4 className="text-center mb-0">Balance Sheet</h4>
          <p className="text-center text-muted mb-3">As on {data.as_on}</p>

          <div className="row g-4">
            {/* ================= ASSETS ================= */}
            <div className="col-6 border-end">
              <table className="table table-sm table-borderless align-middle">
                <thead className="border-bottom table-light">
                  <tr>
                    <th className="fw-bold">Assets</th>
                    <th className="text-end fw-bold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Skip rendering root node header row */}
                  {data.assets?.children?.map((child, index) => (
                    <ReportTreeRows key={index} node={child} level={0} />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold border-top border-bottom table-active">
                    <td>Total Assets</td>
                    <td className="text-end">
                      {formatAmount(data.total_assets)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ============ LIABILITIES & CAPITAL ============ */}
            <div className="col-6">
              <table className="table table-sm table-borderless align-middle">
                <thead className="border-bottom table-light">
                  <tr>
                    <th className="fw-bold">Liabilities & Capital</th>
                    <th className="text-end fw-bold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Liabilities */}
                  <tr className="fw-bold text-uppercase border-bottom text-secondary">
                    <td colSpan={2} className="pt-2">Liabilities</td>
                  </tr>
                  {data.liabilities?.children?.map((child, index) => (
                    <ReportTreeRows key={index} node={child} level={0} />
                  ))}
                  <tr className="fw-bold border-top">
                    <td className="ps-2">Total Liabilities</td>
                    <td className="text-end">
                      {formatAmount(data.total_liabilities)}
                    </td>
                  </tr>

                  {/* Spacing */}
                  <tr>
                    <td colSpan={2} style={{ height: "20px" }}></td>
                  </tr>

                  {/* Capital */}
                  <tr className="fw-bold text-uppercase border-bottom text-secondary">
                    <td colSpan={2}>Capital & Equity</td>
                  </tr>
                  {data.capital?.children?.map((child, index) => (
                    <ReportTreeRows key={index} node={child} level={0} />
                  ))}
                  <tr className="fw-bold border-top">
                    <td className="ps-2">Total Capital</td>
                    <td className="text-end">
                      {formatAmount(data.total_capital)}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="fw-bold border-top border-bottom table-active">
                    <td>Total Liabilities & Capital</td>
                    <td className="text-end">
                      {formatAmount(data.total_liabilities_capital)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Difference Warning */}
          {Math.abs(data.difference) > 0.01 && (
            <div className="alert alert-danger font-bold mt-4 text-center">
              Balance Sheet Mismatch Difference: {formatAmount(data.difference)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
 * Recursive Table Rows Renderer
 * ========================================================= */

function ReportTreeRows({ node, level = 0 }) {
  if (!node) return null;

  // Render Ledger
  if (node.type === "ledger" && node.amount !== 0) {
    return (
      <tr>
        <td style={{ paddingLeft: level * 14 }}>{node.ledger_name}</td>
        <td className="text-end">{formatAmount(node.amount)}</td>
      </tr>
    );
  }

  // Render Group (skip empty zero groups)
  const hasChildren = node.children && node.children.length > 0;
  if (!hasChildren && node.amount === 0) return null;

  return (
    <>
      <tr className="fw-semibold text-dark">
        <td style={{ paddingLeft: level * 14 }}>{node.group_name}</td>
        <td className="text-end">{formatAmount(node.amount)}</td>
      </tr>

      {node.children?.map((child, index) => (
        <ReportTreeRows key={index} node={child} level={level + 1} />
      ))}
    </>
  );
}

/* =========================================================
 * Helpers
 * ========================================================= */

function formatAmount(value) {
  if (value === null || value === undefined) return "0.00";

  const num = Number(value);
  if (isNaN(num)) return "0.00";

  // Retain negative sign for credit/loss balances
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default BalanceSheet;